// ─── CPF ─────────────────────────────────────────────────────────────────────

export function formatCPF(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export function validateCPF(cpf: string): boolean {
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(d)) return false; // todos iguais

  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(d[i]) * (10 - i);
  let rem = (sum * 10) % 11;
  if (rem === 10 || rem === 11) rem = 0;
  if (rem !== parseInt(d[9])) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(d[i]) * (11 - i);
  rem = (sum * 10) % 11;
  if (rem === 10 || rem === 11) rem = 0;
  return rem === parseInt(d[10]);
}

// ─── Telefone ────────────────────────────────────────────────────────────────

export function formatPhone(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export function validatePhone(phone: string): boolean {
  const d = phone.replace(/\D/g, '');
  return d.length === 10 || d.length === 11;
}

// ─── CEP / ViaCEP ────────────────────────────────────────────────────────────

export function formatCEP(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

export interface AddressData {
  cep: string;
  street: string;
  district: string;
  city: string;
  state: string;
}

export async function fetchAddressByCEP(cep: string): Promise<AddressData | null> {
  const digits = cep.replace(/\D/g, '');
  if (digits.length !== 8) return null;

  try {
    const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.erro) return null;

    return {
      cep: data.cep ?? '',
      street: data.logradouro ?? '',
      district: data.bairro ?? '',
      city: data.localidade ?? '',
      state: data.uf ?? '',
    };
  } catch {
    return null;
  }
}

// ─── Cartão de Crédito (Asaas) ──────────────────────────────────────────────

export function formatCardNumber(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 19);
  return d.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

export function formatCardExpiry(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 4);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}/${d.slice(2)}`;
}

/** Luhn — checagem básica antes de mandar pro Asaas; não substitui a validação real do emissor. */
export function validateCardNumber(value: string): boolean {
  const d = value.replace(/\D/g, '');
  if (d.length < 13 || d.length > 19) return false;

  let sum = 0;
  let shouldDouble = false;
  for (let i = d.length - 1; i >= 0; i--) {
    let digit = parseInt(d[i], 10);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
}

/** "MM/AA" digitado no form → { month: "MM", year: "20AA" } pro payload do Asaas. */
export function parseCardExpiry(expiry: string): { month: string; year: string } | null {
  const d = expiry.replace(/\D/g, '');
  if (d.length !== 4) return null;
  const month = d.slice(0, 2);
  const monthNum = parseInt(month, 10);
  if (monthNum < 1 || monthNum > 12) return null;
  return { month, year: `20${d.slice(2)}` };
}

export function isCardExpiryInFuture(month: string, year: string): boolean {
  const now = new Date();
  const expiry = new Date(parseInt(year, 10), parseInt(month, 10), 0, 23, 59, 59);
  return expiry.getTime() >= now.getTime();
}

// ─── Senha ───────────────────────────────────────────────────────────────────
//
// Fonte ÚNICA da política de senha. Telas de definição de senha (cadastro, reset,
// convite, troca logada) devem importar daqui — nunca duplicar a regra inline.
// A política do servidor (Supabase Auth) precisa ser alinhada no dashboard; o
// enforcement client-only é burlável.

export const PASSWORD_POLICY = {
  minLength: 8,
  // bcrypt do Supabase trunca silenciosamente acima de 72 bytes — limitar o input.
  maxLength: 72,
} as const;

// Mensagem única de erro/instrução — evita divergência de texto entre as telas.
export const PASSWORD_POLICY_MESSAGE =
  'A senha precisa ter no mínimo 8 caracteres, incluindo letra maiúscula, letra minúscula, número e símbolo.';

// Símbolo = conjunto explícito de pontuação ASCII. Não usamos /[^A-Za-z0-9]/ de
// propósito: letras acentuadas (á, ç) e espaço NÃO contam como símbolo.
const PASSWORD_SYMBOLS = /[~`!@#$%^&*()_\-+={}[\]|\\:;"'<>,.?/]/;

export interface PasswordCheck {
  minLength: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  symbol: boolean;
}

// Estado por requisito — alimenta o indicador visual em tempo real.
export function checkPassword(password: string): PasswordCheck {
  return {
    minLength: password.length >= PASSWORD_POLICY.minLength,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    symbol: PASSWORD_SYMBOLS.test(password),
  };
}

// Guarda de submit — true só quando todos os requisitos são cumpridos.
export function validatePassword(password: string): boolean {
  const c = checkPassword(password);
  return c.minLength && c.uppercase && c.lowercase && c.number && c.symbol;
}
